import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getApiBase } from "../apiBase";

const MonitorConversationsAdmin = () => {
  const { tenantId, botId } = useParams();

  const [timeRange, setTimeRange] = useState("24h");
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedTurnId, setExpandedTurnId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [botConfig, setBotConfig] = useState(null);

  const TIMING_ORDER = [
  "router",
  "rewriter",
  "embedding",
  "sparse_search",
  "dense_search",
  "hybrid_fusion",
  "reranking",
  "retrieval_flow",
  "generation",
  "total",
];

const TIMING_LABELS = {
  router: "Router",
  rewriter: "Rewriter",
  embedding: "Embedding",
  sparse_search: "Sparse Search",
  dense_search: "Dense Search",
  hybrid_fusion: "Hybrid Fusion",
  reranking: "Reranking",
  retrieval_flow: "Retrieval Flow",
  generation: "Generation",
  total: "Total",
};


  const normalizeTimings = (timings) => {
  if (!timings) return {};

  const flat = { ...timings };

  if (timings.breakdown && typeof timings.breakdown === "object") {
    Object.entries(timings.breakdown).forEach(([k, v]) => {
      flat[k] = v;
    });
    delete flat.breakdown;
  }

  return flat;
};


  const fetchSessions = async () => {
  setLoading(true);
  try {
    const params = new URLSearchParams({ tenant_id: tenantId, bot_id: botId });
    const res = await fetch(`${getApiBase()}/sessions/list?${params}`);
    const json = await res.json();

    const uniqueSessions = Array.from(
      new Map((json.sessions || []).map(s => [s.session_id, s])).values()
    );

    setSessions(uniqueSessions);
  } catch {
    setError("Failed to load sessions");
  } finally {
    setLoading(false);
  }
};

  const openSession = async (session) => {
    setSelectedSession({ ...session, loading: true, data: null, error: null });
    setActiveTab("overview");
    setExpandedTurnId(null);
    try {
      const res = await fetch(
        `${getApiBase()}/sessions/${session.session_id}/stats?tenant_id=${tenantId}&bot_id=${botId}`
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSelectedSession({ ...session, loading: false, data });
    } catch {
      setSelectedSession({ ...session, loading: false, error: "Failed to load stats" });
    }
  };

  const closePanel = () => {
    setSelectedSession(null);
  };

  useEffect(() => {
  let mounted = true;

  const load = async () => {
    if (!mounted) return;
    await fetchSessions();
  };

  load();

  return () => {
    mounted = false;
  };
}, []);

  useEffect(() => {
  if (selectedSession?.session_id) {
    openSession(selectedSession);
  }
}, [botConfig]);


  useEffect(() => {
  fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`)
    .then(res => res.json())
    .then(data => setBotConfig(data.configuration));
}, [tenantId, botId]);

  const today = new Date();

// Start of today (00:00:00.000)
const endOfToday = new Date(today);
endOfToday.setHours(23, 59, 59, 999);

// Start of today
const startOfToday = new Date(today);
startOfToday.setHours(0, 0, 0, 0);

const filteredSessions = sessions.filter((s) => {
  if (timeRange === "all") return true;

  const createdAt = new Date(s.last_activity_at || s.created_at);
  if (isNaN(createdAt)) return false;

  let start = new Date(startOfToday);

  if (timeRange === "3d") {
    start.setDate(start.getDate() - 2); // today + 2 previous full days
  } else if (timeRange === "7d") {
    start.setDate(start.getDate() - 6); // today + 6 previous full days
  }
  return createdAt >= start && createdAt <= endOfToday;
});


  const totalSessionsAll = sessions.length;
  const totalSessionsFiltered = filteredSessions.length;


  const visibleSessions =
  timeRange === "all"
    ? filteredSessions
    : filteredSessions.slice(0, 24);


  const formatDateTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString();
  };

  const formatSeconds = (value) => {
    if (value == null) return "—";
    const num = Number(value);
    const seconds = num > 1000 ? num / 1000 : num;
    return `${seconds.toFixed(3)} s`;
  };



  const formatCost = (cost) => {
    if (cost == null) return "—";
    return `$${Number(cost).toFixed(6)}`;
  };

  return (
    <div className="h-full bg-[#F8F4F2] overflow-auto">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
            <div className="flex flex-col">
              <h1 className="text-xl font-bold text-[#4B2E53]">Sessions</h1>
              <p className="text-xs text-gray-600 mt-0.5">
                <span className="font-semibold text-[#5B3A60]">{totalSessionsFiltered}</span>
                {" "}sessions out of {" "}
                <span className="font-semibold text-[#5B3A60]">{totalSessionsAll}</span>
              </p>
            </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "24h", label: "24h" },
              { key: "3d", label: "3 days" },
              { key: "7d", label: "7 days" },
              { key: "all", label: "All" },
            ].map((r) => (
              <button
                key={r.key}
                onClick={() => setTimeRange(r.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                  timeRange === r.key
                    ? "bg-[#5B3A60] text-white"
                    : "bg-white text-[#5B3A60] border border-[#E6DDD8] hover:bg-[#F3EEF5]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="mb-4 text-red-600 bg-red-50 p-3 rounded">{error}</div>}

        {loading ? (
          <div className="bg-white rounded-lg p-6 text-gray-600">Loading sessions…</div>
        ) : visibleSessions.length === 0 ? (
          <div className="bg-white rounded-lg p-6 text-gray-600">No sessions found</div>
        ) : (
          <div className="bg-white rounded-lg border border-[#E6DDD8] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F4F2]">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Session ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">🔄 Turns</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">📅 Created</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">🕒 Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {visibleSessions.map((s) => (
                  <tr
                    key={s.session_id}
                    className="border-b border-[#E6DDD8] hover:bg-[#F3EEF5] cursor-pointer"
                    onClick={() => openSession(s)}
                  >
                    <td className="py-3 px-4 font-mono text-[#5B3A60] underline">
                      {s.session_id}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1">
                        {s.total_turns || 0}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      {formatDateTime(s.created_at)}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      {formatDateTime(s.last_activity_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedSession && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={closePanel}
          ></div>
          <div className="fixed top-0 right-0 h-full w-[45%] bg-white z-50 shadow-2xl border-l border-[#E6DDD8] flex flex-col">
            <div className="p-5 border-b border-[#E6DDD8]">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-bold text-[#5B3A60] text-lg">Session Details</h2>
                  <p className="text-xs text-gray-600 font-mono mt-1">{selectedSession.session_id}</p>
                </div>
                <button
                  onClick={closePanel}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>

              {selectedSession.data?.summary && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="bg-[#F8F4F2] p-2.5 rounded text-center">
                    <div className="text-gray-600">Turns</div>
                    <div className="font-semibold text-gray-800">{selectedSession.data.summary.total_turns}</div>
                  </div>
                  <div className="bg-[#F8F4F2] p-2.5 rounded text-center">
                    <div className="text-gray-600">Tokens</div>
                    <div className="font-mono text-gray-800">{selectedSession.data.summary.total_tokens}</div>
                  </div>
                  <div className="bg-[#F8F4F2] p-2.5 rounded text-center">
                    <div className="text-gray-600">Cost</div>
                    <div className="font-mono text-[#5B3A60]">
                      {formatCost(selectedSession.data.summary.total_cost_usd)}
                    </div>
                  </div>
                  <div className="bg-[#F8F4F2] p-2.5 rounded text-center">
                    <div className="text-gray-600">Avg Time</div>
                    <div className="font-mono text-gray-800">
                      {formatSeconds(selectedSession.data.summary.average_time_per_turn_ms)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-b border-[#E6DDD8]">
              <div className="flex space-x-6 px-5 py-3">
                {[
                  { id: "overview", label: "Overview", icon: "📊" },
                  { id: "turns", label: "Turns", icon: "🔄" },
                  { id: "retrieval", label: "Retrieval", icon: "🔍" },
                  { id: "system", label: "System", icon: "⚙️" }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 text-sm font-medium pb-2 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? "text-[#5B3A60] border-[#5B3A60]"
                        : "text-gray-600 border-transparent hover:text-gray-800"
                    }`}
                  >
                    <span>{tab.icon}</span> {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {selectedSession.loading ? (
                <div className="text-gray-600">Loading session data…</div>
              ) : selectedSession.error ? (
                <div className="text-red-600">{selectedSession.error}</div>
              ) : !selectedSession.data ? (
                <div className="text-gray-600">No data available</div>
              ) : (
                <>
                  {activeTab === "overview" && (
                    <div className="space-y-5">
                      <div className="bg-white rounded-xl border border-[#E6DDD8] p-5">
                        <h3 className="font-semibold text-[#5B3A60] mb-3">Session Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div><span className="text-gray-600">Tenant ID:</span> <span>{selectedSession.data.summary.tenant_id}</span></div>
                          <div><span className="text-gray-600">Bot ID:</span> <span>{selectedSession.data.summary.bot_id}</span></div>
                          <div><span className="text-gray-600">Total Turns:</span> <span>{selectedSession.data.summary.total_turns}</span></div>
                          <div><span className="text-gray-600">Total Cost:</span> <span className="text-[#5B3A60]">{formatCost(selectedSession.data.summary.total_cost_usd)}</span></div>
                          <div><span className="text-gray-600">Total Tokens:</span> <span>{selectedSession.data.summary.total_tokens}</span></div>
                          <div><span className="text-gray-600">Avg Time per Turn:</span> <span>{formatSeconds(selectedSession.data.summary.average_time_per_turn_ms)}</span></div>
                          <div><span className="text-gray-600">Avg Cost per Turn:</span> <span>{formatCost(selectedSession.data.summary.average_cost_per_turn_usd)}</span></div>
                          <div><span className="text-gray-600">Avg Tokens per Turn:</span> <span>{selectedSession.data.summary.average_tokens_per_turn}</span></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "turns" && (
                    <div className="space-y-4">
                      {selectedSession.data.turns.map((turn) => (
                        <div key={turn.message_id} className="border border-[#E6DDD8] rounded-xl overflow-hidden">
                          <div
                            className="p-4 bg-white cursor-pointer flex justify-between items-center hover:bg-[#F8F4F2]"
                            onClick={() =>
                              setExpandedTurnId(expandedTurnId === turn.message_id ? null : turn.message_id)
                            }
                          >
                            <div className="font-semibold text-[#5B3A60]">Turn {turn.turn_number}</div>
                            <div className="text-sm text-gray-700">
                              {formatSeconds(turn.timings_s?.total)} · {formatCost(turn.costs_usd?.total)}
                            </div>
                          </div>
                          {expandedTurnId === turn.message_id && (
                            <div className="p-5 bg-[#F8F4F2] space-y-5">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-xs text-gray-500 mb-2">👤 User Query</div>
                                  <div className="bg-white p-3 rounded border border-[#E6DDD8] text-sm break-words">{turn.conversation.user_query}</div>
                                </div>
                                {turn.conversation.user_query_rewritten && (
                                  <div>
                                    <div className="text-xs text-gray-500 mb-2">✏️ Rewritten Query</div>
                                    <div className="bg-white p-3 rounded border border-[#E6DDD8] text-sm break-words">{turn.conversation.user_query_rewritten}</div>
                                  </div>
                                )}
                                <div className="md:col-span-2">
                                  {(() => {
                                    const isHumanAgent =
                                      turn.conversation?.response_type === "human_agent_reply" ||
                                      turn.conversation?.response_type === "human_agent";
                                    const sourceLabel = isHumanAgent ? "Human agent" : "AI · RAG";
                                    return (
                                      <>
                                        <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                                          <span>🤖 Response</span>
                                          <span
                                            className={`font-semibold px-1.5 py-0.5 rounded ${
                                              isHumanAgent
                                                ? "bg-[#5B3A60] text-white"
                                                : "bg-[#5B3A60]/10 text-[#5B3A60]"
                                            }`}
                                          >
                                            {sourceLabel}
                                          </span>
                                        </div>
                                        <div
                                          className={`p-3 rounded border text-sm break-words ${
                                            isHumanAgent
                                              ? "bg-[#5B3A60] text-white border-[#5B3A60]"
                                              : "bg-white border-[#E6DDD8]"
                                          }`}
                                        >
                                          {turn.conversation.bot_response}
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3">
                                <div className="bg-white p-2 rounded border border-[#E6DDD8]">
                                  <h4 className="text-xs font-medium text-gray-700 mb-2">🧮 Token Usage</h4>
                                  <div className="space-y-1.5 text-xs">
                                    {Object.entries(turn.tokens).map(([k, v]) =>
                                      v?.total != null ? (
                                        <div key={k} className="flex justify-between">
                                          <span className="text-gray-600">{k.charAt(0).toUpperCase() + k.slice(1)}:</span>
                                          <span className="font-mono">{v.total}</span>
                                        </div>
                                      ) : null
                                    )}
                                  </div>
                                </div>
                                <div className="bg-white p-2 rounded border border-[#E6DDD8]">
                                  <h4 className="text-xs font-medium text-gray-700 mb-2">⏱ Timings</h4>
                                  <div className="space-y-1.5 text-xs">
                                    {TIMING_ORDER.map((k) => {
  const v = normalizeTimings(turn.timings_s)[k];
  if (typeof v !== "number") return null;

  const isTotal = k === "total";

  return (
    <div
      key={k}
      className={`flex justify-between px-0.3 items-center rounded ${
        isTotal ? "bg-[#F3EEF5] border-l-4 border-[#5B3A60]" : ""
      }`}
    >
      <span
        className={`${
          isTotal ? "font-semibold text-[#4B2E53]" : "text-gray-600"
        }`}
      >
        {TIMING_LABELS[k] || k}:
      </span>

      <span
        className={`font-mono ${
          isTotal ? "font-semibold text-[#5B3A60]" : ""
        }`}
      >
        {formatSeconds(v)}
      </span>
    </div>
  );
                                    })}
                                  </div>
                                </div>
                                <div className="bg-white p-2 rounded border border-[#E6DDD8]">
                                  <h4 className="text-xs font-medium text-gray-700 mb-2">💰 Cost (USD)</h4>
                                  <div className="space-y-1.5 text-xs">
                                    {Object.entries(turn.costs_usd).map(([k, v]) =>
                                      typeof v === "number" ? (
                                        <div key={k} className="flex justify-between">
                                          <span className="text-gray-600">{k.charAt(0).toUpperCase() + k.slice(1)}:</span>
                                          <span className="font-mono text-[#5B3A60]">{formatCost(v)}</span>
                                        </div>
                                      ) : null
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white p-4 rounded border border-[#E6DDD8]">
                                <h4 className="text-xs font-medium text-gray-700 mb-2">🧠 Model & Intent</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div><span className="text-gray-600">Model:</span> <span>{turn.llm_config?.model || "—"}</span></div>
                                  <div><span className="text-gray-600">Provider:</span> <span>{turn.llm_config?.provider || "—"}</span></div>
                                  <div><span className="text-gray-600">Detected Intent:</span> <span>{turn.conversation.detected_intent || "—"}</span></div>
                                  <div><span className="text-gray-600">Intent Confidence:</span> <span>{turn.conversation.intent_confidence?.toFixed(3) || "—"}</span></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === "retrieval" && (
                    <div className="space-y-5">
                      {selectedSession.data.turns.map((turn) => (
                        <div key={turn.message_id} className="bg-white rounded-xl border border-[#E6DDD8]">
                          <div className="px-5 py-3 bg-[#F8F4F2] border-b border-[#E6DDD8]">
                            <h3 className="font-semibold text-[#5B3A60]">Turn {turn.turn_number} — Retrieval Details</h3>
                          </div>
                          <div className="p-5 space-y-5">
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                                🧭 Strategy & Routing
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div><span className="text-gray-600">Retrieval Strategy:</span> <span className="font-medium">{turn.retrieval.strategy || "—"}</span></div>
                                <div><span className="text-gray-600">Router Decision:</span> <span className="font-medium">{turn.retrieval.router_decision || "—"}</span></div>
                                <div><span className="text-gray-600">Router Confidence:</span> <span>{turn.retrieval.router_confidence?.toFixed(3) || "—"}</span></div>
                                <div><span className="text-gray-600">Query Rewriter Used:</span> <span>{turn.retrieval.query_rewriter_used ? "Yes" : "No"}</span></div>
                                <div><span className="text-gray-600">Reranker Used:</span> <span>{turn.retrieval.reranker_used ? "Yes" : "No"}</span></div>
                                {turn.retrieval.reranker_api_calls !== undefined && (
                                  <div><span className="text-gray-600">Reranker API Calls:</span> <span>{turn.retrieval.reranker_api_calls}</span></div>
                                )}
                              </div>
                            </div>

                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                                ⚙️ Configuration
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
  <span className="text-gray-600">Top-K Dense (used):</span> {turn.retrieval.top_k_dense}
</div>
<div>
  <span className="text-gray-600">Current Top-K Dense:</span> {botConfig?.k_dense}
</div>
                                <div><span className="text-gray-600">Top-K Sparse:</span> <span>{turn.retrieval.top_k_sparse || "—"}</span></div>
                                <div><span className="text-gray-600">Top-K Final:</span> <span>{turn.retrieval.top_k_final || "—"}</span></div>
                                <div><span className="text-gray-600">Top-K RRF:</span> <span>{turn.retrieval.top_k_rrf || "—"}</span></div>
                                {turn.retrieval.rrf_k !== undefined && (
                                  <div><span className="text-gray-600">RRF k:</span> <span>{turn.retrieval.rrf_k}</span></div>
                                )}
                                <div className="whitespace-nowrap">
                                  <span className="text-gray-600">Retrieval Confidence:</span>
                                  <span>{turn.retrieval.confidence?.toFixed(3) || "—"}</span>
                                </div>      
                              </div>
                            </div>

                            {(turn.retrieval.chunks_retrieved > 0 || turn.retrieval.chunks_reranked > 0) && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                                  📦 Retrieved Chunks
                                </h4>
                                <div className="space-y-3">
                                  <div>
                                    <div className="text-xs text-gray-600 mb-1">Retrieved ({turn.retrieval.chunks_retrieved} chunks)</div>
                                    {turn.retrieval.retrieved_chunk_ids?.length ? (
                                      <div className="flex flex-wrap gap-1.5">
                                        {turn.retrieval.retrieved_chunk_ids.map((id) => (
                                          <span
                                            key={id}
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-[#F3EEF5] text-[#5B3A60] border border-[#E6DDD8]"
                                          >
                                            {id}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-500">None</span>
                                    )}
                                  </div>
                                  {turn.retrieval.reranked_chunk_ids?.length > 0 && (
                                    <div>
                                      <div className="text-xs text-gray-600 mb-1">Reranked ({turn.retrieval.chunks_reranked} chunks)</div>
                                      <div className="flex flex-wrap gap-1.5">
                                        {turn.retrieval.reranked_chunk_ids.map((id) => (
                                          <span
                                            key={id}
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-[#E9E0EB] text-[#5B3A60]"
                                          >
                                            {id}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === "system" && (
  <div className="space-y-6">
    {selectedSession.data.turns.map((turn) => {
      const t = normalizeTimings(turn.timings_s);

      return (
        <div
          key={turn.message_id}
          className="bg-white p-4 rounded-xl border border-[#E6DDD8]"
        >
          <h3 className="font-semibold text-[#5B3A60] mb-3">
            Turn {turn.turn_number}
          </h3>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">CPU Usage:</span>
              <span>{turn.system.cpu_usage_percent?.toFixed(1) || "—"}%</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Memory Usage:</span>
              <span>{turn.system.memory_usage_percent?.toFixed(1) || "—"}%</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Router Latency:</span>
              <span>{formatSeconds(t.router)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Rewriter Latency:</span>
              <span>{formatSeconds(t.rewriter)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Embedding Latency:</span>
              <span>{formatSeconds(t.embedding)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Sparse Search Latency:</span>
              <span>{formatSeconds(t.sparse_search)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Dense Search Latency:</span>
              <span>{formatSeconds(t.dense_search)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Hybrid Fusion Latency:</span>
              <span>{formatSeconds(t.hybrid_fusion)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Reranking Latency:</span>
              <span>{formatSeconds(t.reranking)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Retrieval Latency:</span>
              <span>{formatSeconds(t.retrieval_flow)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Generation Latency:</span>
              <span>{formatSeconds(t.generation)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Total Latency:</span>
              <span>{formatSeconds(t.total)}</span>
            </div>
          </div>
        </div>
      );
    })}
  </div>
)}

                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonitorConversationsAdmin;